import type { TrialEventUpsertRequestDto } from '../../../api/types';

export type EventDraft = Omit<TrialEventUpsertRequestDto, 'trialType'> & {
  trialType: TrialEventUpsertRequestDto['trialType'] | null;
};
export type TrialDraft = EventDraft['actualTrials'][number];
export const stockNames = ['Sheep', 'Cattle', 'Ducks', 'Mixed'] as const;
export const divisions = [
  { field: 'classesIncludeStarted', label: 'Started', types: ['Arena'], required: true },
  { field: 'classesIncludeOpen', label: 'Open', types: ['Arena'], required: true },
  { field: 'classesIncludeAdvanced', label: 'Advanced', types: ['Arena'], required: true },
  { field: 'classesIncludeFtdOpen', label: 'FTD Open', types: ['Farm'], required: true },
  { field: 'classesIncludeFtdAdv', label: 'FTD Advanced', types: ['Farm'], required: true },
  { field: 'classesIncludeNovice', label: 'Novice', types: ['Arena'], required: true },
  { field: 'classesIncludeJunior', label: 'Working Junior Handler', types: ['Arena'], required: true },
  { field: 'classesIncludePostAdvanced', label: 'Post Advanced', types: ['Arena', 'PostAdvanced'], required: false },
  { field: 'classesIncludeRtd', label: 'RTD', types: ['Ranch'], required: false },
] as const;

export function permittedStocks(type: EventDraft['trialType']): readonly string[] {
  if (type === 'Farm') return stockNames;
  if (type === 'Arena') return ['Sheep', 'Cattle', 'Ducks'];
  return type ? ['Sheep', 'Cattle'] : [];
}

export function newEventDraft(): EventDraft {
  return {
    trialType: null,
    offersStockSheep: false, offersStockCattle: false, offersStockDucks: false, offersStockMixed: false,
    allowsFeo: false, actualTrials: [],
    classesIncludeStarted: false, classesIncludeOpen: false, classesIncludeAdvanced: false,
    classesIncludeFtdOpen: false, classesIncludeFtdAdv: false, classesIncludeNovice: false,
    classesIncludeJunior: false, classesIncludePostAdvanced: false, classesIncludeRtd: false,
    name: '', organizationCode: '', sportCode: '', formCode: '', formVersion: '',
    clubSlug: '', eventSlug: '', hostClubName: '', startDate: '', endDate: '',
    addressTitle: '', addressLine1: '', addressCity: '', addressState: '', addressPostalCode: '',
    dogBreeds: '', openToAllBreedsOnSameDate: false,
    preEntriesOpenDateForAustralianShepherds: '', preEntriesOpenDateForOtherBreeds: '', entriesCloseDate: '',
    preEntriesOnly: false, dayOfEntriesStartTime: null, dayOfEntriesEndTime: null, drawForRunOrder: '',
    entryMailAddressLine1: '', entryMailAddressCity: '', entryMailAddressState: '', entryMailAddressPostalCode: '',
    secretaryName: '', secretaryEmail: '',
    weekendDiscountPercent: null, weekendDiscountAmount: null,
    juniorHandlersDiscountPercent: null, juniorHandlersDiscountAmount: null,
  };
}

export function syncOfferSummary(draft: EventDraft): void {
  for (const stock of stockNames) {
    draft[`offersStock${stock}`] = draft.actualTrials.some(trial =>
      trial.stocks?.some(offer => offer.stock === stock));
  }
  draft.totalTrialsAtEvent = draft.actualTrials.length;
}

export function configurationErrors(draft: EventDraft): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  if (!draft.trialType) errors.trialType = ['Choose a trial type.'];
  if (!draft.actualTrials.length) errors.actualTrials = ['Add at least one actual trial.'];
  const slots = new Set<string>();
  draft.actualTrials.forEach((trial, index) => {
    const path = `actualTrials[${index}]`;
    if (!trial.date || trial.date < (draft.startDate ?? '') || trial.date > (draft.endDate ?? '')) {
      errors[`${path}.date`] = ['Choose a date within the event start and end dates.'];
    }
    if (trial.trialNumberOnDate !== 1 && trial.trialNumberOnDate !== 2) {
      errors[`${path}.trialNumberOnDate`] = ['Choose trial 1 (AM) or trial 2 (PM).'];
    }
    const slot = `${trial.date}/${trial.trialNumberOnDate}`;
    if (slots.has(slot)) errors[`${path}.trialNumberOnDate`] = ['This date and trial number already exist.'];
    slots.add(slot);
    trial.stocks?.forEach((offer, stockIndex) => {
      for (const field of ['headCount', 'preEntryFee', 'dayOfFee'] as const) {
        const value = offer[field];
        const numeric = value === null || value === '' ? NaN : Number(value);
        if (!Number.isFinite(numeric) || numeric < 0 ||
            (field === 'headCount' ? !Number.isInteger(numeric) : !/^\d+(\.\d{1,2})?$/.test(String(value)))) {
          errors[`${path}.stocks[${stockIndex}].${field}`] =
            [field === 'headCount' ? 'Enter a nonnegative whole head count; zero is valid.' :
              'Enter a nonnegative USD rate with at most two decimal places; zero is valid.'];
        }
      }
    });
  });
  return errors;
}
